import cn from 'classnames'
import React, { FC, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { RankIcon } from 'partner360/components/partnerRanks/components/rankIcon/RankIcon'
import { StatusBadge } from 'partner360/features/topPartnersManagement/components/statusBadge/StatusBadge'
import { TRANSLATION_KEYS } from 'partner360/features/topPartnersManagement/translations'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { TFlagChangeRequestStatus } from 'partner360/models/partnerFlagRequests/TFlagChangeRequestStatus'

import styles from './PartnerFlagRequestHistoryCard.module.scss'

export type TFlagChangeRequest = Record<string, 1 | 0>

interface IPartnerFlagRequestHistoryCardProps {
  status: TFlagChangeRequestStatus
  createdBy: string
  created: string
  request?: TFlagChangeRequest | null
  partnerFlags: IP360PartnerFlags[]
}

interface IFlagGroupProps {
  type: 'add' | 'remove'
  items: IP360PartnerFlags[]
}

const FlagGroup: FC<IFlagGroupProps> = ({ type, items }) => {
  if (!items.length) {
    return null
  }

  const isAdd = type === 'add'

  const addedTranslation =
    items.length > 1
      ? TRANSLATION_KEYS.topPartners.cards.card.addedFlags
      : TRANSLATION_KEYS.topPartners.cards.card.addedFlag
  const removedTranslation =
    items.length > 1
      ? TRANSLATION_KEYS.topPartners.cards.card.removedFlags
      : TRANSLATION_KEYS.topPartners.cards.card.removedFlag

  const title = isAdd ? addedTranslation : removedTranslation

  return (
    <div className={styles.group}>
      <div
        className={cn(styles.groupTitle, {
          [styles.groupTitleAdd]: isAdd,
          [styles.groupTitleRemove]: !isAdd,
        })}
      >
        <div
          className={cn(styles.groupIcon, {
            [styles.groupIconAdd]: isAdd,
            [styles.groupIconRemove]: !isAdd,
          })}
        >
          {isAdd ? '+' : '−'}
        </div>

        <Translation
          text={title}
          replaceParams={[{ key: '{count}', value: String(items.length) }]}
        />
      </div>

      <div className={styles.flagsList}>
        {items.map((item) => (
          <div key={item.FlagKey} className={styles.flagCard}>
            <div className={styles.flagIcon}>
              <RankIcon partnerFlag={item.PartnerFlag} rankConfig={item} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const PartnerFlagRequestHistoryCard: FC<IPartnerFlagRequestHistoryCardProps> = ({
  status,
  createdBy,
  created,
  request,
  partnerFlags,
}) => {
  const { addedFlags, removedFlags } = useMemo(() => {
    const flagsByKey = Object.fromEntries(partnerFlags.map((flag) => [flag.FlagKey, flag]))

    const added: typeof partnerFlags = []
    const removed: typeof partnerFlags = []

    Object.entries(request || {}).forEach(([flagKey, value]) => {
      const flag = flagsByKey[flagKey]

      if (!flag) {
        return
      }

      if (value === 1) {
        added.push(flag)
        return
      }

      removed.push(flag)
    })

    return {
      addedFlags: added,
      removedFlags: removed,
    }
  }, [partnerFlags, request])

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.partnerRow}>
          <StatusBadge status={status} />
        </div>

        <div className={styles.metaRow}>
          <div className={styles.metaItem}>
            <span className={styles.metaText}>{createdBy}</span>
          </div>

          <div className={styles.metaItem}>
            <span className={styles.metaText}>{getFormatDate(created, 'full')}</span>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <FlagGroup type="add" items={addedFlags} />
        <FlagGroup type="remove" items={removedFlags} />
      </div>
    </div>
  )
}
